import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Search, SlidersHorizontal, Heart, MapPin, X, Home, Plane } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { PhotoImg, VerifiedBadge, OfferChip, GoldButton, SectionTitle } from "../components/common";
import { ChipMultiSelect } from "../components/ChipMultiSelect";
import { MakeOfferModal } from "../components/OfferModals";

function ProfileCard({ c, onFav, onOffer, onOpen }) {
  return (
    <div data-testid={`profile-card-${c.user_id}`} className="group relative rounded-2xl overflow-hidden border border-white/10 bg-[#141113] hover:border-[#D4AF37]/40 transition-colors">
      <div className="relative aspect-[4/5] overflow-hidden cursor-pointer bg-[#1C171A]" onClick={() => onOpen(c.user_id)}>
        <PhotoImg photoId={c.main_photo_id} alt={c.display_name} fallbackInitial={c.display_name?.[0]} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
        <button data-testid={`favorite-btn-${c.user_id}`} onClick={(e) => { e.stopPropagation(); onFav(c); }}
          className={`absolute top-3 right-3 h-9 w-9 rounded-full flex items-center justify-center backdrop-blur-md transition-colors ${c.is_favorite ? "bg-[#751A32] text-white" : "bg-black/40 text-white/80 hover:bg-black/60"}`}>
          <Heart size={17} fill={c.is_favorite ? "currentColor" : "none"} />
        </button>
        {c.verified && <div className="absolute top-3 left-3"><VerifiedBadge selfie={c.selfie_verified} id={c.id_verified} /></div>}
        {c.is_demo && <span data-testid={`demo-badge-${c.user_id}`} className="absolute bottom-3 left-3 rounded-full bg-black/70 border border-[#D4AF37]/50 text-[#D4AF37] px-2.5 py-1 text-[11px] uppercase tracking-wide backdrop-blur-md">{c.account_class === "test" ? "Test Account" : "Demo Profile"}</span>}
      </div>
      <div className="p-4">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-serif text-xl text-[#FBF8F3] leading-tight">{c.display_name}{c.age ? `, ${c.age}` : ""}</h3>
        </div>
        {c.zodiac && <p className="text-[11px] text-[#C9A96E] mt-0.5" data-testid={`card-zodiac-${c.user_id}`}>{c.zodiac.symbol} {c.zodiac.name}</p>}
        {c.location && <p className="text-xs text-[#9E978E] flex items-center gap-1 mt-0.5"><MapPin size={12} />{c.location}</p>}
        <div className="flex flex-wrap items-center gap-2 mt-1">
          {c.is_visiting_now && c.visiting ? (
            <span data-testid={`badge-visiting-${c.user_id}`} className="inline-flex items-center gap-1 rounded-full bg-[#541224]/60 border border-[#D4AF37]/30 text-[#D4AF37] px-2 py-0.5 text-[10px]">
              <Plane size={11} /> Visiting {c.visiting.city}{c.visiting.arrival_date ? ` · ${c.visiting.arrival_date.slice(5)}–${(c.visiting.departure_date||"").slice(5)}` : ""}
            </span>
          ) : (
            <span data-testid={`badge-home-${c.user_id}`} className="inline-flex items-center gap-1 rounded-full bg-[#1C171A] border border-white/10 text-[#9E978E] px-2 py-0.5 text-[10px]"><Home size={11} /> Lives here</span>
          )}
          {c.approx_distance && <span className="text-[11px] text-[#C9A96E]">{c.approx_distance}</span>}
        </div>
        {c.headline && <p className="text-sm text-[#D8D2C9] line-clamp-1 mt-2">{c.headline}</p>}
        <div className="flex flex-wrap gap-1.5 mt-2 mb-3 min-h-[24px]">
          {(c.interests || []).slice(0, 3).map((i) => <span key={i} className="text-[11px] rounded-full bg-[#1C171A] border border-white/8 px-2 py-0.5 text-[#C9A96E]">{i}</span>)}
        </div>
        <div className="flex items-center justify-between gap-2">
          <OfferChip amount={c.preferred_offer} />
          {c.is_demo
            ? <span data-testid={`profile-offers-disabled-${c.user_id}`} className="text-[11px] text-[#9E978E]">Offers disabled</span>
            : <GoldButton data-testid={`profile-place-offer-btn-${c.user_id}`} onClick={() => onOffer(c)} className="px-4 py-2 text-xs">Place a Date Offer</GoldButton>}
        </div>
      </div>
    </div>
  );
}

const fieldCls = "w-full rounded-xl bg-[#0C0A0A]/60 border border-[#D4AF37]/20 focus:border-[#D4AF37]/60 outline-none px-3 py-2.5 text-sm text-[#FBF8F3]";

export default function Discover() {
  const navigate = useNavigate();
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({ gender: "", market: "", visiting_here: false, location: "", age_min: "", age_max: "", offer_min: "", offer_max: "", max_distance: "", verified: false, nearby_week: false, sort: "recent", intentions: [], interests: [], hobbies: [], activities: [] });
  const [markets, setMarkets] = useState([]);
  const [options, setOptions] = useState({ dating_intentions: [], date_activities: [], interests: [], hobbies: [] });
  useEffect(() => { api.get("/markets").then((r) => setMarkets(r.data.markets || [])).catch(() => {}); }, []);
  useEffect(() => { api.get("/profile-options").then((r) => setOptions(r.data || {})).catch(() => {}); }, []);
  const [offerTarget, setOfferTarget] = useState(null);
  const [nearbyCount, setNearbyCount] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { sort: filters.sort };
      if (q) params.q = q;
      if (filters.gender) params.gender = filters.gender;
      if (filters.visiting_here && filters.market) params.visiting_market = filters.market;
      else if (filters.market) params.market = filters.market;
      if (filters.location) params.location = filters.location;
      if (filters.age_min) params.age_min = filters.age_min;
      if (filters.age_max) params.age_max = filters.age_max;
      if (filters.offer_min) params.offer_min = filters.offer_min;
      if (filters.offer_max) params.offer_max = filters.offer_max;
      if (filters.max_distance) params.max_distance = filters.max_distance;
      if (filters.nearby_week) params.nearby_week = true;
      if (filters.verified) params.verified = true;
      if (filters.intentions?.length) params.intentions = filters.intentions.join(",");
      if (filters.interests?.length) params.interests = filters.interests.join(",");
      if (filters.hobbies?.length) params.hobbies = filters.hobbies.join(",");
      if (filters.activities?.length) params.activities = filters.activities.join(",");
      const { data } = await api.get("/discover", { params });
      setResults(data.results);
    } catch (e) { toast.error("Could not load profiles"); }
    finally { setLoading(false); }
  }, [q, filters]);

  const loadCount = useCallback(async () => {
    try {
      const params = {};
      if (q) params.q = q;
      if (filters.gender) params.gender = filters.gender;
      if (filters.market) params.market = filters.market;
      if (filters.location) params.location = filters.location;
      const { data } = await api.get("/discover/nearby-count", { params });
      setNearbyCount(data.count);
    } catch (e) { setNearbyCount(null); }
  }, [q, filters.gender, filters.location]);

  useEffect(() => { load(); loadCount(); }, []);
  useEffect(() => { const t = setTimeout(() => { load(); loadCount(); }, 350); return () => clearTimeout(t); }, [q, filters]);

  const toggleFav = async (c) => {
    try {
      if (c.is_favorite) await api.delete(`/favorites/${c.user_id}`);
      else await api.post(`/favorites/${c.user_id}`);
      setResults((rs) => rs.map((r) => r.user_id === c.user_id ? { ...r, is_favorite: !r.is_favorite } : r));
    } catch (e) { toast.error("Could not update favorite"); }
  };

  const setF = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
        <SectionTitle eyebrow="Discover" title="Remarkable people, waiting to meet" />
        <div className="flex gap-2">
          <div className="relative flex-1 sm:w-72">
            <Search size={17} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#9E978E]" />
            <input data-testid="discovery-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or interest…" className={fieldCls + " pl-10"} />
          </div>
          <button data-testid="toggle-filters-btn" onClick={() => setShowFilters((s) => !s)} className="rounded-xl border border-[#D4AF37]/25 px-4 text-[#D8D2C9] hover:border-[#D4AF37]/60 flex items-center gap-2 text-sm"><SlidersHorizontal size={16} /> Filters</button>
          <button data-testid="nearby-week-toggle" onClick={() => setF("nearby_week", !filters.nearby_week)} className={`rounded-xl px-4 text-sm flex items-center gap-2 border transition-colors ${filters.nearby_week ? "bg-[#541224] border-[#D4AF37]/60 text-[#FBF8F3]" : "border-[#D4AF37]/25 text-[#D8D2C9] hover:border-[#D4AF37]/60"}`}><Plane size={15} /> Nearby This Week{nearbyCount != null ? <span data-testid="nearby-week-count" className="ml-0.5 text-[#D4AF37]">({nearbyCount})</span> : null}</button>
        </div>
      </div>

      {showFilters && (
        <div className="db-glass rounded-2xl border border-[#D4AF37]/20 p-5 mb-7 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 db-fade-up">
          <div>
            <label className="text-[10px] uppercase tracking-wide text-[#C9A96E]">Gender</label>
            <select data-testid="filter-gender-select" value={filters.gender} onChange={(e) => setF("gender", e.target.value)} className={fieldCls}>
              <option value="">Any</option><option>Female</option><option>Male</option><option>Non-binary</option>
            </select>
          </div>
          <div><label className="text-[10px] uppercase tracking-wide text-[#C9A96E]">DateBid Market</label><select data-testid="filter-market-select" value={filters.market} onChange={(e) => setF("market", e.target.value)} className={fieldCls}><option value="">All markets</option>{markets.map((m) => <option key={m.market_id} value={m.market_id}>{m.display_name} ({m.member_count})</option>)}</select>
            {filters.market && <label className="flex items-center gap-1.5 mt-1.5 text-[11px] text-[#C9A96E] cursor-pointer"><input data-testid="filter-visiting-here" type="checkbox" checked={filters.visiting_here} onChange={(e) => setF("visiting_here", e.target.checked)} className="accent-[#D4AF37] h-3.5 w-3.5" /> Visiting here this week</label>}</div>
          <div><label className="text-[10px] uppercase tracking-wide text-[#C9A96E]">Location</label><input data-testid="filter-location" value={filters.location} onChange={(e) => setF("location", e.target.value)} className={fieldCls} placeholder="City" /></div>
          <div><label className="text-[10px] uppercase tracking-wide text-[#C9A96E]">Min Age</label><input data-testid="filter-age-min" type="number" value={filters.age_min} onChange={(e) => setF("age_min", e.target.value)} className={fieldCls} /></div>
          <div><label className="text-[10px] uppercase tracking-wide text-[#C9A96E]">Max Age</label><input data-testid="filter-age-max" type="number" value={filters.age_max} onChange={(e) => setF("age_max", e.target.value)} className={fieldCls} /></div>
          <div><label className="text-[10px] uppercase tracking-wide text-[#C9A96E]">Min Offer</label><input data-testid="filter-offer-min" type="number" value={filters.offer_min} onChange={(e) => setF("offer_min", e.target.value)} className={fieldCls} /></div>
          <div><label className="text-[10px] uppercase tracking-wide text-[#C9A96E]">Max Offer</label><input data-testid="filter-offer-max" type="number" value={filters.offer_max} onChange={(e) => setF("offer_max", e.target.value)} className={fieldCls} /></div>
          <div><label className="text-[10px] uppercase tracking-wide text-[#C9A96E]">Distance</label>
            <select data-testid="filter-max-distance" value={filters.max_distance} onChange={(e) => setF("max_distance", e.target.value)} className={fieldCls}>
              <option value="">Anywhere</option>
              <option value="5">Within 5 mi</option>
              <option value="10">Within 10 mi</option>
              <option value="25">Within 25 mi</option>
              <option value="50">Within 50 mi</option>
              <option value="100">Within 100 mi</option>
            </select>
          </div>
          <div className="col-span-2 sm:col-span-1">
            <label className="text-[10px] uppercase tracking-wide text-[#C9A96E]">Sort</label>
            <select value={filters.sort} onChange={(e) => setF("sort", e.target.value)} className={fieldCls}>
              <option value="recent">Recommended</option><option value="nearest">Nearest first</option><option value="offer_high">Offer: High</option><option value="offer_low">Offer: Low</option>
            </select>
          </div>
          <label className="flex items-center gap-2 text-sm text-[#D8D2C9] self-end pb-2 col-span-2 sm:col-span-1">
            <input data-testid="filter-verified-checkbox" type="checkbox" checked={filters.verified} onChange={(e) => setF("verified", e.target.checked)} className="accent-[#D4AF37]" /> Verified only
          </label>
          <button onClick={() => { setFilters({ gender: "", market: "", visiting_here: false, location: "", age_min: "", age_max: "", offer_min: "", offer_max: "", max_distance: "", verified: false, nearby_week: false, sort: "recent", intentions: [], interests: [], hobbies: [], activities: [] }); setQ(""); }} className="text-xs text-[#9E978E] hover:text-[#FBF8F3] flex items-center gap-1 self-end pb-2.5"><X size={13} /> Clear</button>
        </div>
      )}

      {showFilters && (
        <div className="db-glass rounded-2xl border border-[#D4AF37]/20 p-5 mb-7 grid sm:grid-cols-2 gap-5 db-fade-up">
          <div>
            <label className="text-[10px] uppercase tracking-wide text-[#C9A96E] font-semibold">Dating Intentions</label>
            <ChipMultiSelect testid="filter-intentions" allowOther={false} options={options.dating_intentions || []} value={filters.intentions} onChange={(v) => setF("intentions", v)} />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wide text-[#C9A96E] font-semibold">Ideal Date Activities</label>
            <ChipMultiSelect testid="filter-activities" allowOther={false} options={options.date_activities || []} value={filters.activities} onChange={(v) => setF("activities", v)} />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wide text-[#C9A96E] font-semibold">Interests</label>
            <ChipMultiSelect testid="filter-interests" allowOther={false} options={options.interests || []} value={filters.interests} onChange={(v) => setF("interests", v)} />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wide text-[#C9A96E] font-semibold">Hobbies</label>
            <ChipMultiSelect testid="filter-hobbies" allowOther={false} options={options.hobbies || []} value={filters.hobbies} onChange={(v) => setF("hobbies", v)} />
          </div>
        </div>
      )}

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {Array.from({ length: 8 }).map((_, i) => <div key={i} className="rounded-2xl bg-[#141113] aspect-[3/4] animate-pulse" />)}
        </div>
      ) : results.length === 0 ? (
        <div className="text-center py-24 text-[#9E978E]"><p className="font-serif text-2xl text-[#D8D2C9]">No profiles match your search</p><p className="mt-2 text-sm">Try adjusting your filters.</p></div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {results.map((c) => <ProfileCard key={c.user_id} c={c} onFav={toggleFav} onOffer={setOfferTarget} onOpen={(id) => navigate(`/profile/${id}`)} />)}
        </div>
      )}

      {offerTarget && <MakeOfferModal recipient={offerTarget} onClose={() => setOfferTarget(null)} onDone={load} />}
    </div>
  );
}
